import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const PackageCreationWizard = ({ isOpen, onClose, onSave, editingPackage = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: editingPackage?.title || '',
    destination: editingPackage?.destination || '',
    category: editingPackage?.category || '',
    duration: editingPackage?.duration || '',
    price: editingPackage?.price || '',
    description: editingPackage?.description || '',
    highlights: editingPackage?.highlights || [''],
    inclusions: editingPackage?.inclusions || [''],
    exclusions: editingPackage?.exclusions || [''],
    itinerary: editingPackage?.itinerary || [{ day: 1, title: '', activities: [''] }],
    images: editingPackage?.images || [''],
    status: editingPackage?.status || 'draft'
  });

  const steps = [
    { id: 1, title: 'Basic Information', icon: 'Info' },
    { id: 2, title: 'Itinerary Planning', icon: 'MapPin' },
    { id: 3, title: 'Pricing & Details', icon: 'DollarSign' },
    { id: 4, title: 'Media & Review', icon: 'Image' }
  ];

  const categoryOptions = [
    { value: 'adventure', label: 'Adventure' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'religious', label: 'Religious' },
    { value: 'beach', label: 'Beach' },
    { value: 'hill-station', label: 'Hill Station' },
    { value: 'wildlife', label: 'Wildlife' },
    { value: 'heritage', label: 'Heritage' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleItineraryChange = (dayIndex, field, value, activityIndex = null) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => {
        if (i === dayIndex) {
          if (field === 'activities' && activityIndex !== null) {
            return {
              ...day,
              activities: day.activities.map((activity, j) => 
                j === activityIndex ? value : activity
              )
            };
          }
          return { ...day, [field]: value };
        }
        return day;
      })
    }));
  };

  const addItineraryDay = () => {
    setFormData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { 
        day: prev.itinerary.length + 1, 
        title: '', 
        activities: [''] 
      }]
    }));
  };

  const addActivity = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === dayIndex ? { ...day, activities: [...day.activities, ''] } : day
      )
    }));
  };

  const removeActivity = (dayIndex, activityIndex) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === dayIndex ? {
          ...day,
          activities: day.activities.filter((_, j) => j !== activityIndex)
        } : day
      )
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = () => {
    const packageData = {
      ...formData,
      id: editingPackage?.id || Date.now().toString(),
      lastModified: new Date().toLocaleDateString('en-IN'),
      bookings: editingPackage?.bookings || 0
    };
    onSave(packageData);
    onClose();
  };

  if (!isOpen) return null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Package Title"
                type="text"
                placeholder="Enter package title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
              <Input
                label="Destination"
                type="text"
                placeholder="Enter destination"
                value={formData.destination}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Category"
                options={categoryOptions}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value)}
                placeholder="Select category"
                required
              />
              <Input
                label="Duration"
                type="text"
                placeholder="e.g., 3 Days 2 Nights"
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                rows={4}
                placeholder="Enter package description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Package Highlights
              </label>
              {formData.highlights.map((highlight, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="text"
                    placeholder="Enter highlight"
                    value={highlight}
                    onChange={(e) => handleArrayChange('highlights', index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.highlights.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('highlights', index)}
                      className="w-8 h-8 text-destructive"
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('highlights')}
                iconName="Plus"
                iconPosition="left"
              >
                Add Highlight
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">Day-wise Itinerary</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={addItineraryDay}
                iconName="Plus"
                iconPosition="left"
              >
                Add Day
              </Button>
            </div>

            {formData.itinerary.map((day, dayIndex) => (
              <div key={dayIndex} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-foreground">Day {day.day}</h4>
                  {formData.itinerary.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          itinerary: prev.itinerary.filter((_, i) => i !== dayIndex)
                        }));
                      }}
                      className="w-8 h-8 text-destructive"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  )}
                </div>

                <Input
                  label="Day Title"
                  type="text"
                  placeholder="Enter day title"
                  value={day.title}
                  onChange={(e) => handleItineraryChange(dayIndex, 'title', e.target.value)}
                  className="mb-4"
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Activities
                  </label>
                  {day.activities.map((activity, activityIndex) => (
                    <div key={activityIndex} className="flex items-center space-x-2 mb-2">
                      <Input
                        type="text"
                        placeholder="Enter activity"
                        value={activity}
                        onChange={(e) => handleItineraryChange(dayIndex, 'activities', e.target.value, activityIndex)}
                        className="flex-1"
                      />
                      {day.activities.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeActivity(dayIndex, activityIndex)}
                          className="w-8 h-8 text-destructive"
                        >
                          <Icon name="X" size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addActivity(dayIndex)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add Activity
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Price (₹)"
                type="text"
                placeholder="Enter price"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                required
              />
              <Select
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={(value) => handleInputChange('status', value)}
                placeholder="Select status"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inclusions
              </label>
              {formData.inclusions.map((inclusion, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="text"
                    placeholder="Enter inclusion"
                    value={inclusion}
                    onChange={(e) => handleArrayChange('inclusions', index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.inclusions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('inclusions', index)}
                      className="w-8 h-8 text-destructive"
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('inclusions')}
                iconName="Plus"
                iconPosition="left"
              >
                Add Inclusion
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Exclusions
              </label>
              {formData.exclusions.map((exclusion, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="text"
                    placeholder="Enter exclusion"
                    value={exclusion}
                    onChange={(e) => handleArrayChange('exclusions', index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.exclusions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('exclusions', index)}
                      className="w-8 h-8 text-destructive"
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('exclusions')}
                iconName="Plus"
                iconPosition="left"
              >
                Add Exclusion
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Package Images
              </label>
              {formData.images.map((image, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="url"
                    placeholder="Enter image URL"
                    value={image}
                    onChange={(e) => handleArrayChange('images', index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.images.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('images', index)}
                      className="w-8 h-8 text-destructive"
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('images')}
                iconName="Plus"
                iconPosition="left"
              >
                Add Image
              </Button>
            </div>

            {/* Preview */}
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-lg font-medium text-foreground mb-4">Package Preview</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground">{formData.title}</h4>
                  <p className="text-sm text-muted-foreground">{formData.destination} • {formData.duration}</p>
                  <p className="text-lg font-semibold text-primary mt-2">{formData.price}</p>
                </div>
                
                {formData.description && (
                  <div>
                    <h5 className="font-medium text-foreground mb-1">Description</h5>
                    <p className="text-sm text-muted-foreground">{formData.description}</p>
                  </div>
                )}

                {formData.highlights.filter(h => h.trim()).length > 0 && (
                  <div>
                    <h5 className="font-medium text-foreground mb-1">Highlights</h5>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {formData.highlights.filter(h => h.trim()).map((highlight, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={14} className="text-success" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-prominent w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {editingPackage ? 'Edit Package' : 'Create New Package'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="w-8 h-8"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  currentStep >= step.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {currentStep > step.id ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={step.icon} size={16} />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            {currentStep === steps.length ? (
              <Button
                variant="default"
                onClick={handleSave}
                iconName="Save"
                iconPosition="left"
              >
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageCreationWizard;